import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchMarketingAssetsData,
  addAssetToStore,
  updateAssetInStore,
  removeAssetFromStore,
  addFolderToStore,
  removeFolderFromStore,
  renameFolderCascade,
} from "../../store/marketingAssetsSlice";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  LinearProgress,
  Paper,
  InputAdornment,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
  Badge,
  Divider,
  Avatar,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  FolderOpen as FolderOpenIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckCircleIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  createNewFolder,
  reNameFolder,
  removeFolder,
  getMarketingAssetPresignedUrl,
  uploadMarketingAssetToS3,
  removeFileFromS3Bucket,
  reNameFile,
  getSingleFileDetails,
} from "../../Action/s3UploadApi";
import { getAuthTokenPayload } from "../../Utils/index";

const MarketingAssets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [filterType, setFilterType] = useState("all"); // 'all', 'image', 'video', 'document'
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [favoriteAssets, setFavoriteAssets] = useState([]);
  const [newAsset, setNewAsset] = useState({
    name: "",
    description: "",
    tags: "",
    file: null,
  });
  const [csvPreview, setCsvPreview] = useState({
    rows: [],
    loading: false,
    error: "",
  });
  
  // Context menu states
  const [contextMenu, setContextMenu] = useState(null);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newMenuAnchorEl, setNewMenuAnchorEl] = useState(null);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderMenuAnchorEl, setFolderMenuAnchorEl] = useState(null);
  const [folderMenuPosition, setFolderMenuPosition] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [renameFileDialogOpen, setRenameFileDialogOpen] = useState(false);
  const [renameFileName, setRenameFileName] = useState("");
  const fileInputRef = useRef(null);

  // Redux — Marketing Assets
  const dispatch = useDispatch();
  const assets = useSelector((state) => state.marketingAssets.assets);
  const folders = useSelector((state) => state.marketingAssets.folders);
  const maStatus = useSelector((state) => state.marketingAssets.status);
  const maError = useSelector((state) => state.marketingAssets.error);
  // foldersLoading covers both the initial API fetch and folder CRUD mutations
  const [_folderMutating, setFoldersLoading] = useState(false);
  const foldersLoading = maStatus === "loading" || _folderMutating;
  const foldersError = maError || "";

  const normalizeFolderPath = (path) => {
    if (!path) return "";
    return path.endsWith("/") ? path : `${path}/`;
  };

  const normalizeS3Prefix = (prefix) => {
    if (!prefix) return "";
    const trimmed = prefix.replace(/^\/+/, "");
    return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
  };

  const resolveFileUrl = (item) => {
    const directUrl = item.url || item.s3Url || item.presignedUrl;
    if (directUrl) return directUrl;
    if (item.s3Path && /^https?:\/\//i.test(item.s3Path)) return item.s3Path;
    const publicBase = process.env.REACT_APP_S3_PUBLIC_BASE_URL;
    if (publicBase && item.s3Path) {
      const trimmedBase = publicBase.replace(/\/+$/, "");
      const trimmedPath = item.s3Path.replace(/^\/+/, "");
      return `${trimmedBase}/${trimmedPath}`;
    }
    return item.s3Path || "";
  };

  const getAssetUrl = (asset) => {
    if (!asset) return "";
    return asset.url || resolveFileUrl({ s3Path: asset.s3Path }) || "";
  };

  const isUsablePreviewUrl = (url) => /^(https?:|blob:|data:)/i.test(url || "");

  const formatDisplayDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const parseCsvText = (text, maxRows = 50) => {
    const rows = [];
    let row = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === "," && !inQuotes) {
        row.push(current);
        current = "";
        continue;
      }

      if ((char === "\n" || char === "\r") && !inQuotes) {
        if (char === "\r" && nextChar === "\n") {
          i += 1;
        }
        row.push(current);
        if (row.some((cell) => cell !== "")) {
          rows.push(row);
        }
        if (rows.length >= maxRows) {
          return rows;
        }
        row = [];
        current = "";
        continue;
      }

      current += char;
    }

    row.push(current);
    if (row.some((cell) => cell !== "")) {
      rows.push(row);
    }

    return rows.slice(0, maxRows);
  };

  // Fetch folders and files — only on first mount or after explicit invalidation
  useEffect(() => {
    if (maStatus === "idle") {
      dispatch(fetchMarketingAssetsData());
    }
  }, [maStatus, dispatch]);

  useEffect(() => {
    const fileName = selectedAsset?.name || "";
    const isCsv = /\.(csv)$/i.test(fileName);
    if (!previewDialogOpen || !selectedAsset || !isCsv) {
      setCsvPreview({ rows: [], loading: false, error: "" });
      return;
    }

    const previewUrl = getAssetUrl(selectedAsset);
    if (!previewUrl || !isUsablePreviewUrl(previewUrl)) {
      setCsvPreview({
        rows: [],
        loading: false,
        error: "CSV preview requires a public or local URL.",
      });
      return;
    }

    let isActive = true;
    setCsvPreview({ rows: [], loading: true, error: "" });

    fetch(previewUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load CSV (${response.status}).`);
        }
        return response.text();
      })
      .then((text) => {
        if (!isActive) return;
        const rows = parseCsvText(text, 100);
        setCsvPreview({
          rows,
          loading: false,
          error: rows.length ? "" : "CSV is empty.",
        });
      })
      .catch((error) => {
        if (!isActive) return;
        setCsvPreview({
          rows: [],
          loading: false,
          error: error?.message || "Failed to load CSV preview.",
        });
      });

    return () => {
      isActive = false;
    };
  }, [previewDialogOpen, selectedAsset]);

  // Filter assets based on filter type, folder path, and search
  useEffect(() => {
    let filtered = assets;

    const normalizedCurrentPath = normalizeFolderPath(currentFolderPath);
    if (normalizedCurrentPath) {
      filtered = filtered.filter(
        (asset) => normalizeFolderPath(asset.folderPath) === normalizedCurrentPath
      );
    } else {
      filtered = filtered.filter(
        (asset) => !normalizeFolderPath(asset.folderPath)
      );
    }

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter((asset) => asset.type === filterType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          asset.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          asset.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredAssets(filtered);
  }, [filterType, searchQuery, assets, currentFolderPath]);

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
  };

  const handleUploadClick = () => {
    // isStorageFull is computed in the render body; re-evaluate here for safety
    const _mList = getAuthTokenPayload()?.menuList ?? [];
    const _mEntry = _mList.find((m) => m.menuId === "MENU-009");
    const _lStr = _mEntry?.storageLimit || null;
    const _parseBytes = (s) => {
      if (!s) return null;
      const mt = String(s).match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);
      if (!mt) return null;
      const v = parseFloat(mt[1]);
      const u = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
      return v * (u[mt[2].toUpperCase()] || 1);
    };
    const _lBytes = _parseBytes(_lStr);
    if (_lBytes !== null) {
      const _used = assets.reduce((s, a) => s + (a.sizeBytes || 0), 0);
      if (_used >= _lBytes) {
        setSnackbar({
          open: true,
          message: `Storage limit of ${_lStr} has been reached. Please remove files before uploading.`,
          severity: "error",
        });
        return;
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleUploadClose = () => {
    setUploadDialogOpen(false);
    setNewAsset({ name: "", description: "", tags: "", file: null });
    setUploadProgress(0);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewAsset({ ...newAsset, file, name: file.name });
      setUploadDialogOpen(false);
      setUploadProgress(0);
      handleUploadSubmit(file);
    }
  };

  const handleUploadSubmit = async (selectedFile) => {
    const fileToUpload = selectedFile || newAsset.file;
    if (!fileToUpload) {
      setSnackbar({
        open: true,
        message: "Please select a file to upload.",
        severity: "warning",
      });
      return;
    }

    // Check storage limit from MENU-009 token
    const _menuList = getAuthTokenPayload()?.menuList ?? [];
    const _maMenu = _menuList.find((m) => m.menuId === "MENU-009");
    const _limitStr = _maMenu?.storageLimit || null;
    const _parsedLimitBytes = (() => {
      if (!_limitStr) return null;
      const m = String(_limitStr).match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);
      if (!m) return null;
      const v = parseFloat(m[1]);
      const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
      return v * (units[m[2].toUpperCase()] || 1);
    })();
    if (_parsedLimitBytes !== null) {
      const _currentUsedBytes = assets.reduce((sum, a) => sum + (a.sizeBytes || 0), 0);
      if (_currentUsedBytes + (fileToUpload.size || 0) > _parsedLimitBytes) {
        const _usedFormatted = formatFileSize(_currentUsedBytes);
        setSnackbar({
          open: true,
          message: `Storage limit reached. You have used ${_usedFormatted} of ${_limitStr} allowed. Please remove some files to free up space.`,
          severity: "error",
        });
        return;
      }
    }

    setUploading(true);
    setUploadProgress(20);

    try {
      const normalizedFolder = normalizeS3Prefix(currentFolderPath);
      const folderPath = normalizedFolder ? `/${normalizedFolder.replace(/\/+$/g, "")}` : "/";

      const presignedResponse = await getMarketingAssetPresignedUrl(
        fileToUpload.name,
        folderPath
      );

      if (presignedResponse?.status === false) {
        setSnackbar({
          open: true,
          message:
            presignedResponse?.uiMsg ||
            presignedResponse?.message ||
            "Failed to get upload URL.",
          severity: "warning",
        });
        setUploadProgress(0);
        return;
      }

      const presignedData = presignedResponse?.data || presignedResponse || {};

      const uploadUrl =
        presignedData?.uploadUrl ||
        presignedData?.presignedUrl ||
        presignedData?.url;

      if (!uploadUrl) {
        throw new Error("Upload URL not provided.");
      }

      setUploadProgress(60);
      await uploadMarketingAssetToS3(uploadUrl, fileToUpload);

      setUploadProgress(80);

      const s3Path =
        presignedData?.s3Path ||
        presignedData?.filePath ||
        `${normalizedFolder}${fileToUpload.name}`;

      // Create a temporary blob URL for immediate preview
      const localBlobUrl = URL.createObjectURL(fileToUpload);
      
      // Construct proper view URL from upload response or S3 path
      const viewUrl = presignedData?.downloadUrl || 
                      presignedData?.viewUrl || 
                      presignedData?.fileUrl || 
                      presignedData?.publicUrl ||
                      resolveFileUrl({ 
                        s3Path, 
                        url: presignedData?.fileUrl || presignedData?.publicUrl 
                      });

      setUploadProgress(100);

      const newAssetData = {
        id: s3Path || `${Date.now()}-${fileToUpload.name}`,
        name: fileToUpload.name,
        type: getFileType(fileToUpload.name),
        url: viewUrl || localBlobUrl, // Use viewUrl first, fallback to local blob
        presignedUrl: viewUrl,
        localBlobUrl: localBlobUrl, // Keep local blob as backup
        size: formatFileSize(fileToUpload.size),
        sizeBytes: fileToUpload.size || 0,
        uploadDate: new Date().toISOString(),
        tags: newAsset.tags
          ? newAsset.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
          : [],
        description: newAsset.description,
        folderPath: normalizeFolderPath(currentFolderPath),
        s3Path,
      };

      // Add to store immediately with blob URL for instant display
      dispatch(addAssetToStore(newAssetData));
      
      // Try to fetch proper presigned URL in background (if API exists)
      // This will update the asset with the proper URL
      getSingleFileDetails(s3Path)
        .then((detailsResponse) => {
          if (detailsResponse?.status !== false && detailsResponse?.data) {
            const properUrl = resolveFileUrl(detailsResponse.data);
            if (properUrl && properUrl !== localBlobUrl) {
              // Update the asset in store with the proper presigned URL
              dispatch(updateAssetInStore({ id: newAssetData.id, url: properUrl, presignedUrl: properUrl }));
              // Clean up the blob URL once we have the real URL
              URL.revokeObjectURL(localBlobUrl);
            }
          }
        })
        .catch((err) => {
          console.warn("Background fetch of file details failed:", err);
          // No problem - we'll use the blob URL or whatever URL we have
        });

      setSnackbar({
        open: true,
        message: "Asset uploaded successfully!",
        severity: "success",
      });
      setNewAsset({ name: "", description: "", tags: "", file: null });
      setUploadProgress(0);
    } catch (error) {
      const uiMessage =
        error?.response?.data?.uiMsg ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload asset. Please try again.";
      setSnackbar({
        open: true,
        message: uiMessage,
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const getFileType = (filename) => {
    const ext = filename?.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext))
      return "image";
    if (["mp4", "avi", "mov", "wmv", "webm"].includes(ext)) return "video";
    return "document";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleAssetClick = (asset) => {
    setSelectedAsset(asset);
    setPreviewDialogOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewDialogOpen(false);
    setSelectedAsset(null);
  };

  const handleMenuClick = (event, asset) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAsset(asset);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFileRenameOpen = () => {
    if (!selectedAsset) return;
    setRenameFileName(selectedAsset.name || "");
    setRenameFileDialogOpen(true);
    handleMenuClose();
  };

  const handleFileRenameClose = () => {
    setRenameFileDialogOpen(false);
    setRenameFileName("");
  };

  const handleFileRenameSave = async () => {
    if (!selectedAsset) return;
    const trimmedName = renameFileName.trim();
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter a file name",
        severity: "warning",
      });
      return;
    }

    const currentPath = selectedAsset.s3Path
      ? `/${selectedAsset.s3Path.replace(/^\/+/, "")}`
      : `/${normalizeS3Prefix(selectedAsset.folderPath || "")}${selectedAsset.name}`.replace(
          /\/+/g,
          "/"
        );

    const pathParts = currentPath.replace(/^\/+/, "").split("/").filter(Boolean);
    const apiPath = `/${pathParts.slice(2).join("/")}`;

    try {
      const response = await reNameFile(apiPath, trimmedName);
      if (response?.status === false) {
        setSnackbar({
          open: true,
          message: response?.uiMsg || response?.message || "Failed to rename file.",
          severity: "warning",
        });
        return;
      }

      const newPath = currentPath.replace(/[^/]+$/, trimmedName).replace(/\/+/, "/");

      dispatch(
        updateAssetInStore({
          id: selectedAsset.id,
          name: trimmedName,
          s3Path: selectedAsset.s3Path ? newPath.replace(/^\//, "") : selectedAsset.s3Path,
        })
      );
      setSelectedAsset((prev) =>
        prev
          ? {
              ...prev,
              name: trimmedName,
              s3Path: prev.s3Path ? newPath.replace(/^\//, "") : prev.s3Path,
            }
          : prev
      );
      setSnackbar({
        open: true,
        message: response?.uiMsg || "File renamed successfully!",
        severity: "success",
      });
      handleFileRenameClose();
    } catch (error) {
      const uiMessage =
        error?.response?.data?.uiMsg ||
        error?.response?.data?.message ||
        "Failed to rename file. Please try again.";
      setSnackbar({
        open: true,
        message: uiMessage,
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    const asset = selectedAsset;
    if (!asset) return;

    const normalizedFolder = normalizeS3Prefix(asset.folderPath || "");
    const resolvedPath = asset.s3Path
      ? `/${asset.s3Path.replace(/^\/+/, "")}`
      : `/${normalizedFolder}${asset.name}`.replace(/\/+/g, "/");

    try {
      const response = await removeFileFromS3Bucket(resolvedPath);
      if (response?.status === false) {
        setSnackbar({
          open: true,
          message: response?.uiMsg || response?.message || "Failed to delete file.",
          severity: "warning",
        });
        handleMenuClose();
        return;
      }

      dispatch(removeAssetFromStore(asset.id));
      setSnackbar({
        open: true,
        message: response?.uiMsg || "Asset deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      const uiMessage =
        error?.response?.data?.uiMsg ||
        error?.response?.data?.message ||
        "Failed to delete file. Please try again.";
      setSnackbar({
        open: true,
        message: uiMessage,
        severity: "error",
      });
    } finally {
      handleMenuClose();
    }
  };

  const handleDownload = () => {
    const asset = selectedAsset;
    const url = getAssetUrl(asset);
    if (!url || !isUsablePreviewUrl(url)) {
      setSnackbar({
        open: true,
        message:
          "Preview/download URL not available. Please configure a public file URL.",
        severity: "warning",
      });
      handleMenuClose();
      return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = asset?.name || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({
      open: true,
      message: "Downloading asset...",
      severity: "info",
    });
    handleMenuClose();
  };

  const handleCopyLink = () => {
    const url = getAssetUrl(selectedAsset);
    if (!url || !isUsablePreviewUrl(url)) {
      setSnackbar({
        open: true,
        message: "Link not available for this file.",
        severity: "warning",
      });
      handleMenuClose();
      return;
    }

    navigator.clipboard.writeText(url).then(
      () => {
        setSnackbar({
          open: true,
          message: "Link copied to clipboard!",
          severity: "success",
        });
      },
      () => {
        setSnackbar({
          open: true,
          message: "Unable to copy link.",
          severity: "error",
        });
      }
    );
    handleMenuClose();
  };

  const toggleFavorite = (assetId) => {
    setFavoriteAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  // Handle right-click context menu
  const handleContextMenu = (event) => {
    event.preventDefault();

    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleUploadNewFile = () => {
    handleCloseContextMenu();
    handleUploadClick();
  };

  const handleCreateNewFolder = () => {
    handleCloseContextMenu();
    setCreateFolderDialogOpen(true);
  };

  const handleCloseFolderDialog = () => {
    setCreateFolderDialogOpen(false);
    setNewFolderName("");
  };

  const handleNewMenuClick = (event) => {
    setNewMenuAnchorEl(event.currentTarget);
  };

  const handleNewMenuClose = () => {
    setNewMenuAnchorEl(null);
  };

  const handleUploadFromMenu = () => {
    handleNewMenuClose();
    handleUploadClick();
  };

  const handleCreateFolderFromMenu = () => {
    handleNewMenuClose();
    setCreateFolderDialogOpen(true);
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a folder name",
        severity: "warning",
      });
      return;
    }

    const normalizedPath = normalizeFolderPath(normalizeS3Prefix(currentFolderPath));
    const parentFolderPath = normalizedPath ? `/${normalizedPath}` : "/";
    const folderName = newFolderName.trim();

    setFoldersLoading(true);
    createNewFolder(parentFolderPath, folderName)
      .then((response) => {
        if (response?.status === false) {
          setSnackbar({
            open: true,
            message: response?.uiMsg || response?.message || "Failed to create folder.",
            severity: "warning",
          });
          return;
        }

        const folderPath = `${normalizedPath}${folderName}/`;
        dispatch(
          addFolderToStore({
            id: `${folderPath}-${Date.now()}`,
            folderName,
            parentFolderPath: normalizedPath,
            folderPath,
          })
        );
        setSnackbar({
          open: true,
          message: response?.uiMsg || "Folder created successfully!",
          severity: "success",
        });
        handleCloseFolderDialog();
      })
      .catch((error) => {
        const uiMessage =
          error?.response?.data?.uiMsg ||
          error?.response?.data?.message ||
          "Failed to create folder. Please try again.";
        setSnackbar({
          open: true,
          message: uiMessage,
          severity: "error",
        });
      })
      .finally(() => {
        setFoldersLoading(false);
      });
  };

  const handleFolderOpen = (folder) => {
    const nextPath = folder.folderPath || `${normalizeFolderPath(folder.parentFolderPath)}${folder.folderName}/`;
    setCurrentFolderPath(normalizeFolderPath(nextPath));
  };

  const handleFolderSelect = (event, folder) => {
    event.stopPropagation();
    setSelectedFolderId(folder.id);
  };

  const handleFolderMenuOpen = (event, folder) => {
    event.stopPropagation();
    setSelectedFolderId(folder.id);
    setSelectedFolder(folder);
    setFolderMenuAnchorEl(event.currentTarget);
    setFolderMenuPosition(null);
  };

  const handleFolderContextMenu = (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedFolderId(folder.id);
    setSelectedFolder(folder);
    setFolderMenuAnchorEl(null);
    setFolderMenuPosition({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleFolderMenuClose = () => {
    setFolderMenuAnchorEl(null);
    setFolderMenuPosition(null);
  };

  const handleFolderDownload = () => {
    setSnackbar({
      open: true,
      message: "Downloading folder...",
      severity: "info",
    });
    handleFolderMenuClose();
  };

  const handleFolderRemove = () => {
    if (!selectedFolder) return;
    const apiFolderPath = `/${normalizeS3Prefix(selectedFolder.folderPath).replace(/\/+$/g, "")}`;

    setFoldersLoading(true);
    removeFolder(apiFolderPath)
      .then(() => {
        dispatch(removeFolderFromStore(selectedFolder.id));
        setSelectedFolderId(null);
        setSnackbar({
          open: true,
          message: "Folder removed successfully!",
          severity: "success",
        });
        handleFolderMenuClose();
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Failed to remove folder. Please try again.",
          severity: "error",
        });
      })
      .finally(() => {
        setFoldersLoading(false);
      });
  };

  const handleFolderRenameOpen = () => {
    if (!selectedFolder) return;
    setRenameFolderName(selectedFolder.folderName || "");
    setRenameDialogOpen(true);
    handleFolderMenuClose();
  };

  const handleFolderRenameClose = () => {
    setRenameDialogOpen(false);
    setRenameFolderName("");
  };

  const handleFolderRenameSave = async () => {
    if (!selectedFolder) return;
    const trimmedName = renameFolderName.trim();
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter a folder name",
        severity: "warning",
      });
      return;
    }

    const apiFolderPath = `/${normalizeS3Prefix(selectedFolder.folderPath).replace(/\/+$/g, "")}`;

    setFoldersLoading(true);
    try {
      const response = await reNameFolder(trimmedName, apiFolderPath);

      if (response?.status === false) {
        setSnackbar({
          open: true,
          message: response?.uiMsg || response?.message || "Failed to rename folder.",
          severity: "warning",
        });
        return;
      }

      const oldPath = normalizeFolderPath(
        selectedFolder.folderPath ||
          `${normalizeFolderPath(selectedFolder.parentFolderPath)}${selectedFolder.folderName}/`
      );
      const newPath = `${normalizeFolderPath(selectedFolder.parentFolderPath)}${trimmedName}/`;

      dispatch(renameFolderCascade({ folderId: selectedFolder.id, oldPath, newPath, newFolderName: trimmedName }));

      setCurrentFolderPath((prev) =>
        prev?.startsWith(oldPath) ? normalizeFolderPath(prev.replace(oldPath, newPath)) : prev
      );

      setSelectedFolder((prev) =>
        prev
          ? {
              ...prev,
              folderName: trimmedName,
              folderPath: newPath,
            }
          : prev
      );

      setSnackbar({
        open: true,
        message: "Folder renamed successfully!",
        severity: "success",
      });
      handleFolderRenameClose();
    } catch (error) {
      const uiMessage =
        error?.response?.data?.uiMsg ||
        error?.response?.data?.message ||
        "Failed to rename folder. Please try again.";
      setSnackbar({
        open: true,
        message: uiMessage,
        severity: "error",
      });
    } finally {
      setFoldersLoading(false);
    }
  };

  const getFileIcon = (type, isLarge = false) => {
    const size = isLarge ? 60 : 40;
    switch (type) {
      case "image":
        return <ImageIcon sx={{ fontSize: size, color: "#3b82f6" }} />;
      case "video":
        return <VideoIcon sx={{ fontSize: size, color: "#8b5cf6" }} />;
      case "document":
        return <DocumentIcon sx={{ fontSize: size, color: "#10b981" }} />;
      default:
        return <FileIcon sx={{ fontSize: size, color: "#9ca3af" }} />;
    }
  };

  const getStats = () => {
    const totalAssets = assets.length;
    const images = assets.filter(a => a.type === "image").length;
    const videos = assets.filter(a => a.type === "video").length;
    const documents = assets.filter(a => a.type === "document").length;
    return { totalAssets, images, videos, documents };
  };

  const stats = getStats();
  const normalizedCurrentPath = normalizeFolderPath(currentFolderPath);
  const baseFolders = searchQuery
    ? folders
    : folders.filter(
        (folder) => normalizeFolderPath(folder.parentFolderPath) === normalizedCurrentPath
      );
  const visibleFolders = baseFolders.filter((folder) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      folder.folderName?.toLowerCase().includes(query) ||
      folder.parentFolderPath?.toLowerCase().includes(query)
    );
  });
  const hasAssets = filteredAssets.length > 0;
  const hasFolders = visibleFolders.length > 0;

  const breadcrumbParts = normalizedCurrentPath
    .split("/")
    .filter(Boolean);

  // --- Storage limit from MENU-009 token ---
  const parseStorageToBytes = (limitStr) => {
    if (!limitStr) return null;
    const match = String(limitStr).match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
    return value * (units[match[2].toUpperCase()] || 1);
  };
  const marketingAssetsMenu = (() => {
    const menuList = getAuthTokenPayload()?.menuList ?? [];
    return menuList.find((m) => m.menuId === "MENU-009") || null;
  })();
  const storageLimitLabel = marketingAssetsMenu?.storageLimit || null;
  const storageLimitBytes = parseStorageToBytes(storageLimitLabel);
  const usedStorageBytes = assets.reduce((sum, a) => sum + (a.sizeBytes || 0), 0);
  const storageUsedPercent = storageLimitBytes
    ? Math.min((usedStorageBytes / storageLimitBytes) * 100, 100)
    : 0;
  const isStorageFull = storageLimitBytes !== null && usedStorageBytes >= storageLimitBytes;
  const storageBarColor =
    storageUsedPercent >= 90 ? "#ef4444" : storageUsedPercent >= 70 ? "#f59e0b" : "#10b981";
  // Per-type bytes for segmented bar
  const imageBytesUsed = assets.filter(a => a.type === "image").reduce((s, a) => s + (a.sizeBytes || 0), 0);
  const videoBytesUsed = assets.filter(a => a.type === "video").reduce((s, a) => s + (a.sizeBytes || 0), 0);
  const docBytesUsed   = assets.filter(a => a.type === "document").reduce((s, a) => s + (a.sizeBytes || 0), 0);
  const toPercent = (bytes) => storageLimitBytes ? Math.min((bytes / storageLimitBytes) * 100, 100) : 0;

  const isDarkTheme = localStorage.getItem("theme") === "dark";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: isDarkTheme ? "#0b1120" : "#f1f5f9",
        backgroundImage: isDarkTheme
          ? "radial-gradient(1200px circle at 10% 10%, rgba(59,130,246,0.12), transparent 40%), radial-gradient(1000px circle at 90% 0%, rgba(139,92,246,0.12), transparent 45%)"
          : "radial-gradient(1200px circle at 10% 10%, rgba(59,130,246,0.08), transparent 40%), radial-gradient(1000px circle at 90% 0%, rgba(16,185,129,0.08), transparent 45%)",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Enhanced Header with Stats */}
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 3, md: 4 },
            p: { xs: 2, sm: 3 },
            borderRadius: 4,
            bgcolor: isDarkTheme ? "rgba(15, 23, 42, 0.72)" : "#ffffff",
            border: `1px solid ${isDarkTheme ? "#1e293b" : "#e2e8f0"}`,
            boxShadow: isDarkTheme
              ? "0 20px 60px rgba(15, 23, 42, 0.45)"
              : "0 20px 60px rgba(15, 23, 42, 0.08)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "flex-start" },
              mb: { xs: 2, sm: 3 },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: isDarkTheme ? "#fff" : "#1e293b",
                  mb: 0.5,
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                }}
              >
                Marketing Assets
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  color: isDarkTheme ? "#94a3b8" : "#64748b",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Manage and organize all your marketing materials efficiently
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Tooltip
                title={isStorageFull ? `Storage limit of ${storageLimitLabel} reached. Delete files to upload.` : ""}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                    startIcon={!isMobile && <UploadIcon />}
                    onClick={handleUploadClick}
                    disabled={isStorageFull}
                    sx={{
                      bgcolor: isDarkTheme ? "#8b5cf6" : "#3b82f6",
                      fontWeight: 600,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1.2, sm: 1.5 },
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor: isDarkTheme ? "#7c3aed" : "#2563eb",
                        boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
                        transform: "translateY(-2px)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: isDarkTheme ? "#374151" : "#cbd5e1",
                        color: isDarkTheme ? "#6b7280" : "#94a3b8",
                        boxShadow: "none",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isMobile ? <><UploadIcon sx={{ mr: 1 }} /> Upload</> : "Upload"}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                size={isMobile ? "medium" : "large"}
                startIcon={<CreateNewFolderIcon />}
                onClick={handleNewMenuClick}
                sx={{
                  borderColor: isDarkTheme ? "#8b5cf6" : "#3b82f6",
                  color: isDarkTheme ? "#8b5cf6" : "#3b82f6",
                  fontWeight: 600,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.2, sm: 1.5 },
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: isDarkTheme ? "#7c3aed" : "#2563eb",
                    bgcolor: isDarkTheme ? "rgba(139, 92, 246, 0.1)" : "rgba(59, 130, 246, 0.1)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {isMobile ? "New" : "New Folder"}
              </Button>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  bgcolor: isDarkTheme ? "#111827" : "#fff",
                  backgroundImage: isDarkTheme
                    ? "linear-gradient(135deg, rgba(59,130,246,0.08), transparent 55%)"
                    : "linear-gradient(135deg, rgba(59,130,246,0.08), transparent 55%)",
                  border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                  boxShadow: isDarkTheme
                    ? "0 12px 28px rgba(15, 23, 42, 0.45)"
                    : "0 12px 28px rgba(15, 23, 42, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: isDarkTheme
                      ? "0 18px 40px rgba(15, 23, 42, 0.6)"
                      : "0 18px 40px rgba(15, 23, 42, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column", sm: "row" }, textAlign: { xs: "center", sm: "left" } }}>
                  <Avatar
                    sx={{
                      bgcolor: "#eff6ff",
                      color: "#3b82f6",
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                    }}
                  >
                    <FolderOpenIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isDarkTheme ? "#fff" : "#1e293b",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {stats.totalAssets}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      }}
                    >
                      Total Assets
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  bgcolor: isDarkTheme ? "#111827" : "#fff",
                  backgroundImage: isDarkTheme
                    ? "linear-gradient(135deg, rgba(2,132,199,0.08), transparent 55%)"
                    : "linear-gradient(135deg, rgba(2,132,199,0.08), transparent 55%)",
                  border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                  boxShadow: isDarkTheme
                    ? "0 12px 28px rgba(15, 23, 42, 0.45)"
                    : "0 12px 28px rgba(15, 23, 42, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: isDarkTheme
                      ? "0 18px 40px rgba(15, 23, 42, 0.6)"
                      : "0 18px 40px rgba(15, 23, 42, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column", sm: "row" }, textAlign: { xs: "center", sm: "left" } }}>
                  <Avatar
                    sx={{
                      bgcolor: "#f0f9ff",
                      color: "#0284c7",
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                    }}
                  >
                    <ImageIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isDarkTheme ? "#fff" : "#1e293b",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {stats.images}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      }}
                    >
                      Images
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  bgcolor: isDarkTheme ? "#111827" : "#fff",
                  backgroundImage: isDarkTheme
                    ? "linear-gradient(135deg, rgba(139,92,246,0.08), transparent 55%)"
                    : "linear-gradient(135deg, rgba(139,92,246,0.08), transparent 55%)",
                  border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                  boxShadow: isDarkTheme
                    ? "0 12px 28px rgba(15, 23, 42, 0.45)"
                    : "0 12px 28px rgba(15, 23, 42, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: isDarkTheme
                      ? "0 18px 40px rgba(15, 23, 42, 0.6)"
                      : "0 18px 40px rgba(15, 23, 42, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column", sm: "row" }, textAlign: { xs: "center", sm: "left" } }}>
                  <Avatar
                    sx={{
                      bgcolor: "#faf5ff",
                      color: "#8b5cf6",
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                    }}
                  >
                    <VideoIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isDarkTheme ? "#fff" : "#1e293b",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {stats.videos}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      }}
                    >
                      Videos
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  bgcolor: isDarkTheme ? "#111827" : "#fff",
                  backgroundImage: isDarkTheme
                    ? "linear-gradient(135deg, rgba(16,185,129,0.08), transparent 55%)"
                    : "linear-gradient(135deg, rgba(16,185,129,0.08), transparent 55%)",
                  border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                  boxShadow: isDarkTheme
                    ? "0 12px 28px rgba(15, 23, 42, 0.45)"
                    : "0 12px 28px rgba(15, 23, 42, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: isDarkTheme
                      ? "0 18px 40px rgba(15, 23, 42, 0.6)"
                      : "0 18px 40px rgba(15, 23, 42, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column", sm: "row" }, textAlign: { xs: "center", sm: "left" } }}>
                  <Avatar
                    sx={{
                      bgcolor: "#f0fdf4",
                      color: "#10b981",
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                    }}
                  >
                    <DocumentIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isDarkTheme ? "#fff" : "#1e293b",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {stats.documents}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      }}
                    >
                      Documents
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Storage Usage — Google Drive style */}
          {storageLimitBytes !== null && (
            <Box
              sx={{
                mt: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 3 },
                pt: { xs: 2, sm: 2.5 },
                pb: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: isDarkTheme ? "rgba(15,23,42,0.6)" : "#fff",
                border: `1px solid ${
                  isStorageFull
                    ? "rgba(239,68,68,0.3)"
                    : storageUsedPercent >= 70
                    ? "rgba(245,158,11,0.25)"
                    : isDarkTheme ? "#1e293b" : "#e2e8f0"
                }`,
                boxShadow: isDarkTheme
                  ? "0 4px 20px rgba(0,0,0,0.25)"
                  : "0 2px 12px rgba(15,23,42,0.06)",
              }}
            >
              {/* ── Row 1: title left · used/total right ── */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.75, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: isDarkTheme ? "#f1f5f9" : "#1e293b", fontSize: "0.85rem", letterSpacing: "0.01em" }}
                  >
                    Storage
                  </Typography>
                  {isStorageFull && (
                    <Chip label="Limit Reached" size="small"
                      sx={{ bgcolor: "#ef4444", color: "#fff", height: 18, fontSize: "0.58rem", fontWeight: 700, "& .MuiChip-label": { px: 1 } }}
                    />
                  )}
                  {!isStorageFull && storageUsedPercent >= 80 && (
                    <Chip label="Almost Full" size="small"
                      sx={{ bgcolor: "#f59e0b", color: "#fff", height: 18, fontSize: "0.58rem", fontWeight: 700, "& .MuiChip-label": { px: 1 } }}
                    />
                  )}
                </Box>
                {/* used / limit — right aligned, prominent */}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: isDarkTheme ? "#e2e8f0" : "#1e293b" }}>
                    {formatFileSize(usedStorageBytes)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, color: isDarkTheme ? "#475569" : "#94a3b8" }}>
                    / {storageLimitLabel}
                  </Typography>
                  <Typography sx={{
                    fontSize: "0.72rem", fontWeight: 700,
                    color: storageBarColor,
                    ml: 0.5,
                    px: 0.75, py: 0.1,
                    borderRadius: 1,
                    bgcolor: isStorageFull
                      ? "rgba(239,68,68,0.1)"
                      : storageUsedPercent >= 70
                      ? "rgba(245,158,11,0.1)"
                      : "rgba(16,185,129,0.1)",
                  }}>
                    {storageUsedPercent.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>

              {/* ── Row 2: segmented bar ── */}
              <Box
                sx={{
                  height: 10,
                  borderRadius: 99,
                  overflow: "hidden",
                  bgcolor: isDarkTheme ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)",
                  display: "flex",
                }}
              >
                {/* Images segment */}
                {imageBytesUsed > 0 && (
                  <Box sx={{
                    width: `${toPercent(imageBytesUsed)}%`,
                    background: "linear-gradient(90deg, #3b82f6, #2563eb)",
                    transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)",
                    borderRadius: videoBytesUsed === 0 && docBytesUsed === 0 ? "99px" : "0",
                  }} />
                )}
                {/* Videos segment */}
                {videoBytesUsed > 0 && (
                  <Box sx={{
                    width: `${toPercent(videoBytesUsed)}%`,
                    background: "linear-gradient(90deg, #8b5cf6, #7c3aed)",
                    transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)",
                  }} />
                )}
                {/* Documents segment */}
                {docBytesUsed > 0 && (
                  <Box sx={{
                    width: `${toPercent(docBytesUsed)}%`,
                    background: isStorageFull
                      ? "linear-gradient(90deg, #ef4444, #dc2626)"
                      : storageUsedPercent >= 70
                      ? "linear-gradient(90deg, #f59e0b, #d97706)"
                      : "linear-gradient(90deg, #10b981, #059669)",
                    transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)",
                    borderRadius: "0 99px 99px 0",
                  }} />
                )}
                {/* If nothing uploaded yet, show a tiny stub */}
                {usedStorageBytes === 0 && (
                  <Box sx={{ width: "0.3%", bgcolor: isDarkTheme ? "#334155" : "#cbd5e1", borderRadius: 99 }} />
                )}
              </Box>

              {/* ── Row 3: legend left · remaining right ── */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1.5,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {/* Legend dots */}
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
                  {[{label: "Images", color: "#3b82f6", bytes: imageBytesUsed},
                    {label: "Videos", color: "#8b5cf6", bytes: videoBytesUsed},
                    {label: "Documents", color: isStorageFull ? "#ef4444" : storageUsedPercent >= 70 ? "#f59e0b" : "#10b981", bytes: docBytesUsed},
                  ].map(({ label, color, bytes }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.6, opacity: bytes > 0 ? 1 : 0.35 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.68rem", color: isDarkTheme ? "#94a3b8" : "#64748b", lineHeight: 1 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: isDarkTheme ? "#cbd5e1" : "#334155", lineHeight: 1 }}>
                        {formatFileSize(bytes)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Remaining / full message */}
                {isStorageFull ? (
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#ef4444" }}>
                    No space — delete files to upload
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: isDarkTheme ? "#94a3b8" : "#475569" }}>
                      {formatFileSize(storageLimitBytes - usedStorageBytes)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: isDarkTheme ? "#475569" : "#94a3b8" }}>
                      remaining
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Full alert */}
              {isStorageFull && (
                <Alert severity="error"
                  sx={{ mt: 1.5, py: 0.5, fontSize: "0.78rem", borderRadius: 2,
                    bgcolor: isDarkTheme ? "rgba(239,68,68,0.08)" : undefined,
                    border: "1px solid rgba(239,68,68,0.22)" }}
                >
                  Storage limit of <strong>{storageLimitLabel}</strong> reached. Delete unused files to upload new ones.
                </Alert>
              )}
            </Box>
          )}
        </Paper>

        {/* Enhanced Search and Controls */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: isDarkTheme ? "rgba(15, 23, 42, 0.7)" : "#fff",
            border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
            boxShadow: isDarkTheme
              ? "0 16px 40px rgba(15, 23, 42, 0.4)"
              : "0 16px 40px rgba(15, 23, 42, 0.1)",
            backdropFilter: "blur(8px)",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              placeholder={isMobile ? "Search..." : "Search by name, tags, or description..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                flex: 1, 
                minWidth: { xs: "auto", sm: 300 },
                "& .MuiOutlinedInput-root": {
                  bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isDarkTheme ? "#64748b" : "#94a3b8", fontSize: { xs: 18, sm: 24 } }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              value={filterType}
              onChange={handleFilterChange}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                minWidth: { xs: 120, sm: 160 },
                "& .MuiOutlinedInput-root": {
                  bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterIcon sx={{ color: isDarkTheme ? "#64748b" : "#94a3b8", fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FolderOpenIcon sx={{ fontSize: 18 }} />
                  <span>All Assets ({stats.totalAssets})</span>
                </Box>
              </MenuItem>
              <MenuItem value="image">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ImageIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <span>Images ({stats.images})</span>
                </Box>
              </MenuItem>
              <MenuItem value="video">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VideoIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                  <span>Videos ({stats.videos})</span>
                </Box>
              </MenuItem>
              <MenuItem value="document">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DocumentIcon sx={{ fontSize: 18, color: "#10b981" }} />
                  <span>Documents ({stats.documents})</span>
                </Box>
              </MenuItem>
            </TextField>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
              <Tooltip title="Grid View">
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  onClick={() => setViewMode("grid")}
                  sx={{
                    bgcolor: viewMode === "grid" ? (isDarkTheme ? "#3b82f6" : "#dbeafe") : "transparent",
                    color: viewMode === "grid" ? (isDarkTheme ? "#fff" : "#3b82f6") : (isDarkTheme ? "#94a3b8" : "#64748b"),
                    "&:hover": {
                      bgcolor: viewMode === "grid" ? (isDarkTheme ? "#2563eb" : "#bfdbfe") : (isDarkTheme ? "#334155" : "#f1f5f9"),
                    },
                  }}
                >
                  <GridViewIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  onClick={() => setViewMode("list")}
                  sx={{
                    bgcolor: viewMode === "list" ? (isDarkTheme ? "#3b82f6" : "#dbeafe") : "transparent",
                    color: viewMode === "list" ? (isDarkTheme ? "#fff" : "#3b82f6") : (isDarkTheme ? "#94a3b8" : "#64748b"),
                    "&:hover": {
                      bgcolor: viewMode === "list" ? (isDarkTheme ? "#2563eb" : "#bfdbfe") : (isDarkTheme ? "#334155" : "#f1f5f9"),
                    },
                  }}
                >
                  <ListViewIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        <Box
          onContextMenu={handleContextMenu}
          sx={{
            minHeight: { xs: "50vh", md: "60vh" },
            pb: { xs: 4, sm: 6 },
          }}
        >
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
          {/* Folders Section */}
          {foldersLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          )}
          {foldersError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {foldersError}
            </Alert>
          )}
          <Breadcrumbs
            separator=">"
            sx={{
              mb: 2,
              color: isDarkTheme ? "#94a3b8" : "#64748b",
              fontWeight: 600,
              px: { xs: 1, sm: 1.5 },
              py: 0.75,
              borderRadius: 999,
              bgcolor: isDarkTheme ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
              border: `1px solid ${isDarkTheme ? "#1f2937" : "#e2e8f0"}`,
              boxShadow: isDarkTheme
                ? "0 12px 28px rgba(15, 23, 42, 0.4)"
                : "0 12px 28px rgba(15, 23, 42, 0.08)",
              "& .MuiBreadcrumbs-separator": {
                mx: 1,
                color: isDarkTheme ? "#64748b" : "#94a3b8",
              },
            }}
          >
            <Button
              size="small"
              onClick={() => setCurrentFolderPath("")}
              sx={{
                textTransform: "none",
                color: isDarkTheme ? "#e2e8f0" : "#111827",
                fontWeight: 700,
                minWidth: "auto",
                px: 0.5,
              }}
            >
              My Drive
            </Button>
            {breadcrumbParts.map((part, index) => {
              const isLast = index === breadcrumbParts.length - 1;
              const path = `${breadcrumbParts.slice(0, index + 1).join("/")}/`;
              return isLast ? (
                <Typography
                  key={`${part}-${index}`}
                  variant="body2"
                  sx={{ color: isDarkTheme ? "#e2e8f0" : "#111827", fontWeight: 700 }}
                >
                  {part}
                </Typography>
              ) : (
                <Button
                  key={`${part}-${index}`}
                  size="small"
                  onClick={() => setCurrentFolderPath(path)}
                  sx={{
                    textTransform: "none",
                    color: isDarkTheme ? "#cbd5f5" : "#111827",
                    fontWeight: 600,
                    minWidth: "auto",
                    px: 0.5,
                  }}
                >
                  {part}
                </Button>
              );
            })}
          </Breadcrumbs>
          {hasFolders && (
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: isDarkTheme ? "#fff" : "#1e293b",
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                  }}
                >
                  Folders
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}
                >
                  {visibleFolders.length} folders
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {visibleFolders.map((folder) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={folder.id}
                  >
                    <Card
                      onClick={(event) => handleFolderSelect(event, folder)}
                      onDoubleClick={() => handleFolderOpen(folder)}
                      onContextMenu={(event) => handleFolderContextMenu(event, folder)}
                      sx={{
                        borderRadius: 3,
                        bgcolor: isDarkTheme ? "#0f172a" : "#fff",
                        backgroundImage: isDarkTheme
                          ? "linear-gradient(135deg, rgba(59,130,246,0.12), transparent 45%)"
                          : "linear-gradient(135deg, rgba(59,130,246,0.08), transparent 45%)",
                        border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                        boxShadow: isDarkTheme
                          ? "0 14px 32px rgba(15, 23, 42, 0.45)"
                          : "0 14px 32px rgba(15, 23, 42, 0.08)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: isDarkTheme ? "#3b82f6" : "#3b82f6",
                          boxShadow: isDarkTheme
                            ? "0 18px 40px rgba(59, 130, 246, 0.22)"
                            : "0 18px 40px rgba(15, 23, 42, 0.16)",
                        },
                        ...(selectedFolderId === folder.id
                          ? {
                              borderColor: "#3b82f6",
                              boxShadow: isDarkTheme
                                ? "0 0 0 2px rgba(59, 130, 246, 0.35)"
                                : "0 0 0 2px rgba(59, 130, 246, 0.25)",
                            }
                          : {}),
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: isDarkTheme ? "rgba(59, 130, 246, 0.18)" : "#eff6ff",
                              color: isDarkTheme ? "#93c5fd" : "#3b82f6",
                              width: 46,
                              height: 46,
                            }}
                          >
                            <FolderIcon />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                color: isDarkTheme ? "#fff" : "#1e293b",
                                lineHeight: 1.2,
                              }}
                              noWrap
                            >
                              {folder.folderName}
                            </Typography>
                            {/* <Typography
                              variant="caption"
                              sx={{
                                color: isDarkTheme ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Parent: {folder.parentFolderPath || "root"}
                            </Typography> */}
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(event) => handleFolderMenuOpen(event, folder)}
                          sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Assets Grid/List */}
          {!hasAssets && !hasFolders ? (
            <Paper
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: 3,
                bgcolor: isDarkTheme ? "rgba(15, 23, 42, 0.7)" : "#fff",
                border: isDarkTheme
                  ? "2px dashed #444"
                  : "2px dashed #d1d5db",
                boxShadow: isDarkTheme
                  ? "0 18px 40px rgba(15, 23, 42, 0.45)"
                  : "0 18px 40px rgba(15, 23, 42, 0.08)",
                backdropFilter: "blur(8px)",
                cursor: "context-menu",
              }}
            >
              <FolderIcon
                sx={{
                  fontSize: 80,
                  color: isDarkTheme ? "#4b5563" : "#9ca3af",
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: isDarkTheme ? "#9ca3af" : "#6b7280",
                  mb: 1,
                }}
              >
                No assets found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkTheme ? "#6b7280" : "#9ca3af", mb: 2 }}
              >
                Upload your first marketing asset to get started
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkTheme ? "#64748b" : "#94a3b8",
                  fontStyle: "italic",
                }}
              >
                💡 Tip: Right-click anywhere to upload files or create folders, or use the buttons above
              </Typography>
            </Paper>
          ) : (
            <>
              {hasAssets && (
                <>
                {viewMode === "list" && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isDarkTheme ? "#0f172a" : "#fff",
                      border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(280px, 1.4fr) 0.6fr 0.7fr 0.4fr 48px",
                        gap: 2,
                        alignItems: "center",
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Name
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Owner
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Date modified
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>
                        File size
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>
                        Actions
                      </Typography>
                    </Box>
                  </Paper>
                )}
                <Grid container spacing={viewMode === "list" ? 1 : 2}>
                {filteredAssets.map((asset) => (
                <Grid
                  item
                  xs={12}
                  sm={viewMode === "grid" ? 6 : 12}
                  md={viewMode === "grid" ? 4 : 12}
                  lg={viewMode === "grid" ? 3 : 12}
                  key={asset.id}
                >
                  {viewMode === "list" ? (
                    <Paper
                      elevation={0}
                      onClick={() => handleAssetClick(asset)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: isDarkTheme ? "#111827" : "#fff",
                        border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                        display: "grid",
                        gridTemplateColumns: "minmax(280px, 1.4fr) 0.6fr 0.7fr 0.4fr 48px",
                        gap: 2,
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: isDarkTheme ? "#3b82f6" : "#3b82f6",
                          boxShadow: isDarkTheme
                            ? "0 8px 20px rgba(59, 130, 246, 0.25)"
                            : "0 8px 20px rgba(15, 23, 42, 0.08)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        {asset.type === "image" ? (
                          <Avatar
                            variant="rounded"
                            src={getAssetUrl(asset)}
                            alt={asset.name}
                            sx={{ width: 34, height: 34, bgcolor: "#e2e8f0" }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{ width: 34, height: 34, bgcolor: isDarkTheme ? "#0f172a" : "#f1f5f9" }}
                          >
                            {getFileIcon(asset.type, false)}
                          </Avatar>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isDarkTheme ? "#e2e8f0" : "#111827",
                          }}
                          noWrap
                        >
                          {asset.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: isDarkTheme ? "#cbd5e1" : "#475569" }}>
                        {asset.owner || "me"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDarkTheme ? "#cbd5e1" : "#475569" }}>
                        {formatDisplayDate(asset.uploadDate)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: isDarkTheme ? "#cbd5e1" : "#475569", textAlign: "right" }}
                      >
                        {asset.size}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Tooltip title="More actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, asset)}
                            sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ) : (
                  <Card
                    sx={{
                      borderRadius: 3,
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      bgcolor: isDarkTheme ? "#1e293b" : "#fff",
                      border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                      overflow: "hidden",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: isDarkTheme
                          ? "0 20px 40px rgba(59, 130, 246, 0.3)"
                          : "0 20px 40px rgba(0, 0, 0, 0.12)",
                        borderColor: isDarkTheme ? "#3b82f6" : "#3b82f6",
                      },
                    }}
                    onClick={() => handleAssetClick(asset)}
                  >
                  {/* Image/Preview Section */}
                  <Box
                    sx={{
                      height: viewMode === "grid" ? 160 : 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: asset.type === "image" 
                        ? "transparent" 
                        : (isDarkTheme ? "#0f172a" : "#f1f5f9"),
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {asset.type === "image" ? (
                      <>
                        <CardMedia
                          component="img"
                          height={viewMode === "grid" ? "160" : "120"}
                          image={getAssetUrl(asset)}
                          alt={asset.name}
                          sx={{ 
                            objectFit: "cover",
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                            }
                          }}
                        />
                        {/* Overlay Badge */}
                        <Chip
                          label={asset.format}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            bgcolor: "rgba(0,0,0,0.75)",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            backdropFilter: "blur(8px)",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        {getFileIcon(asset.type, true)}
                        <Chip
                          label={asset.format}
                          size="small"
                          sx={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            bgcolor: isDarkTheme ? "#334155" : "#fff",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </>
                    )}
                    
                    {/* Action Buttons Overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 0.5,
                        opacity: 1,
                      }}
                    >
                      <Tooltip title="Favorite">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(asset.id);
                          }}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.95)",
                            backdropFilter: "blur(8px)",
                            "&:hover": { 
                              bgcolor: "#fff",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {favoriteAssets.includes(asset.id) ? (
                            <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                          ) : (
                            <StarBorderIcon sx={{ color: "#64748b", fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, asset)}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.95)",
                            backdropFilter: "blur(8px)",
                            "&:hover": { 
                              bgcolor: "#fff",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <MoreVertIcon sx={{ color: "#64748b", fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Content Section */}
                  <CardContent sx={{ p: 2, pb: 1.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: isDarkTheme ? "#fff" : "#1e293b",
                          lineHeight: 1.3,
                        }}
                        noWrap
                      >
                        {asset.name}
                      </Typography>
                    </Box>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkTheme ? "#94a3b8" : "#64748b",
                        mb: 1,
                        minHeight: 32,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {asset.description}
                    </Typography>

                    {/* Tags */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        mb: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {asset.tags.slice(0, 3).map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            bgcolor: isDarkTheme ? "#334155" : "#f1f5f9",
                            color: isDarkTheme ? "#94a3b8" : "#475569",
                            fontWeight: 500,
                            "&:hover": {
                              bgcolor: isDarkTheme ? "#475569" : "#e2e8f0",
                            },
                          }}
                        />
                      ))}
                      {asset.tags.length > 3 && (
                        <Chip
                          label={`+${asset.tags.length - 3}`}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            bgcolor: isDarkTheme ? "#3b82f6" : "#dbeafe",
                            color: isDarkTheme ? "#fff" : "#3b82f6",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Footer Info */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {asset.type === "image" && (
                          <Chip
                            label={asset.dimensions}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.65rem",
                              height: 22,
                              borderColor: isDarkTheme ? "#334155" : "#e2e8f0",
                              color: isDarkTheme ? "#94a3b8" : "#64748b",
                            }}
                          />
                        )}
                        {asset.type === "video" && (
                          <Chip
                            label={asset.duration}
                            size="small"
                            variant="outlined"
                            icon={<VideoIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              fontSize: "0.65rem",
                              height: 22,
                              borderColor: isDarkTheme ? "#334155" : "#e2e8f0",
                              color: isDarkTheme ? "#94a3b8" : "#64748b",
                            }}
                          />
                        )}
                        {asset.type === "document" && asset.pages && (
                          <Chip
                            label={`${asset.pages} pages`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.65rem",
                              height: 22,
                              borderColor: isDarkTheme ? "#334155" : "#e2e8f0",
                              color: isDarkTheme ? "#94a3b8" : "#64748b",
                            }}
                          />
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{ 
                            color: isDarkTheme ? "#64748b" : "#94a3b8",
                            fontWeight: 600,
                          }}
                        >
                          {asset.size}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Quick Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: "auto",
                        pt: 1,
                      }}
                    >
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload();
                          }}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
                            color: isDarkTheme ? "#94a3b8" : "#64748b",
                            "&:hover": {
                              bgcolor: isDarkTheme ? "#1e293b" : "#f1f5f9",
                              color: isDarkTheme ? "#3b82f6" : "#3b82f6",
                            },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy Link">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyLink();
                          }}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
                            color: isDarkTheme ? "#94a3b8" : "#64748b",
                            "&:hover": {
                              bgcolor: isDarkTheme ? "#1e293b" : "#f1f5f9",
                              color: isDarkTheme ? "#3b82f6" : "#3b82f6",
                            },
                          }}
                        >
                          <CopyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Share">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSnackbar({
                              open: true,
                              message: "Share functionality coming soon!",
                              severity: "info",
                            });
                          }}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
                            color: isDarkTheme ? "#94a3b8" : "#64748b",
                            "&:hover": {
                              bgcolor: isDarkTheme ? "#1e293b" : "#f1f5f9",
                              color: isDarkTheme ? "#3b82f6" : "#3b82f6",
                            },
                          }}
                        >
                          <ShareIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
                  )}
              </Grid>
              ))}
            </Grid>
            </>
            )}
          </>
        )}
        </Box>
      </Container>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: isDarkTheme ? "#fff" : "#1e293b",
            bgcolor: isDarkTheme ? "#1e1e1e" : "#fff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          Upload New Asset
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <input
              accept="image/*,video/*,.pdf,.doc,.docx"
              style={{ display: "none" }}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{
                  py: 3,
                  px: 4,
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 2,
                  width: "100%",
                }}
              >
                {newAsset.file ? newAsset.file.name : "Choose File"}
              </Button>
            </label>
          </Box>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                {uploadProgress}%
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Asset Name"
            value={newAsset.name}
            onChange={(e) =>
              setNewAsset({ ...newAsset, name: e.target.value })
            }
            sx={{ mb: 2 }}
            disabled={uploading}
          />

          <TextField
            fullWidth
            label="Description"
            value={newAsset.description}
            onChange={(e) =>
              setNewAsset({ ...newAsset, description: e.target.value })
            }
            multiline
            rows={3}
            sx={{ mb: 2 }}
            disabled={uploading}
          />

          <TextField
            fullWidth
            label="Tags (comma separated)"
            value={newAsset.tags}
            onChange={(e) =>
              setNewAsset({ ...newAsset, tags: e.target.value })
            }
            placeholder="e.g., logo, branding, social"
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3, 
            pt: 2,
            borderTop: "1px solid #e2e8f0",
            gap: 1
          }}
        >
          <Button 
            onClick={handleUploadClose} 
            disabled={uploading}
            variant="outlined"
            sx={{ 
              color: "#64748b",
              borderColor: "#e2e8f0",
              "&:hover": {
                borderColor: "#cbd5e1",
                backgroundColor: "#f8fafc"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={!newAsset.file || uploading}
            sx={{
              bgcolor: "#4B5AB6",
              "&:hover": { bgcolor: "#3a4891" },
              "&:disabled": {
                bgcolor: "#cbd5e1",
                color: "#94a3b8"
              },
              px: 3,
              py: 1
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedAsset?.name}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "75vh", overflowY: "auto" }}>
          {selectedAsset && (() => {
            const previewUrl = getAssetUrl(selectedAsset);
            if (!previewUrl || !isUsablePreviewUrl(previewUrl)) {
              return (
                <Box
                  sx={{
                    height: 320,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f9fafb",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={1} alignItems="center">
                    <FileIcon sx={{ fontSize: 80, color: "#9ca3af" }} />
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Preview not available
                    </Typography>
                  </Stack>
                </Box>
              );
            }

            if (selectedAsset.type === "image") {
              return (
                <Box
                  component="img"
                  src={previewUrl}
                  alt={selectedAsset.name}
                  sx={{
                    width: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    borderRadius: 2,
                    bgcolor: "#0b1120",
                  }}
                />
              );
            }

            if (selectedAsset.type === "video") {
              return (
                <Box
                  component="video"
                  src={previewUrl}
                  controls
                  sx={{ width: "100%", maxHeight: "60vh", borderRadius: 2 }}
                />
              );
            }

            if (selectedAsset.type === "document") {
              const fileName = selectedAsset?.name || "";
              const isDocx = /\.(docx?|DOCX?)$/.test(fileName);
              const isCsv = /\.(csv)$/i.test(fileName);
              const isPublicUrl = /^https?:\/\//i.test(previewUrl || "");
              if (isCsv) {
                if (csvPreview.loading) {
                  return (
                    <Box sx={{ py: 3 }}>
                      <LinearProgress />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", textAlign: "center", mt: 1 }}
                      >
                        Loading CSV preview...
                      </Typography>
                    </Box>
                  );
                }

                if (csvPreview.error) {
                  return (
                    <Box
                      sx={{
                        height: 320,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f9fafb",
                        borderRadius: 2,
                      }}
                    >
                      <Stack spacing={1} alignItems="center">
                        <FileIcon sx={{ fontSize: 80, color: "#9ca3af" }} />
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {csvPreview.error}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                }

                const [headerRow, ...dataRows] = csvPreview.rows;
                return (
                  <TableContainer sx={{ maxHeight: "60vh" }}>
                    <Table stickyHeader size="small">
                      {headerRow && (
                        <TableHead>
                          <TableRow>
                            {headerRow.map((cell, index) => (
                              <TableCell key={`csv-head-${index}`}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody>
                        {dataRows.map((row, rowIndex) => (
                          <TableRow key={`csv-row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                              <TableCell key={`csv-cell-${rowIndex}-${cellIndex}`}>
                                {cell}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              }

              if (isDocx) {
                if (!isPublicUrl) {
                  return (
                    <Box
                      sx={{
                        height: 320,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f9fafb",
                        borderRadius: 2,
                      }}
                    >
                      <Stack spacing={1} alignItems="center">
                        <FileIcon sx={{ fontSize: 80, color: "#9ca3af" }} />
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          DOC/DOCX preview requires a public URL
                        </Typography>
                      </Stack>
                    </Box>
                  );
                }

                const viewerUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
                  previewUrl
                )}`;
                return (
                  <Box
                    component="iframe"
                    src={viewerUrl}
                    title={selectedAsset.name}
                    sx={{ width: "100%", height: "60vh", border: "none", borderRadius: 2 }}
                  />
                );
              }

              return (
                <Box
                  component="iframe"
                  src={previewUrl}
                  title={selectedAsset.name}
                  sx={{ width: "100%", height: "60vh", border: "none", borderRadius: 2 }}
                />
              );
            }

            return null;
          })()}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {selectedAsset?.description}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              {selectedAsset?.tags.map((tag, index) => (
                <Chip key={index} label={tag} size="small" />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Size: {selectedAsset?.size} • Uploaded:{" "}
              {formatDisplayDate(selectedAsset?.uploadDate)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            variant="outlined"
          >
            Download
          </Button>
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDownload}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleFileRenameOpen}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={() => handleAssetClick(selectedAsset)}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Right-Click Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        onContextMenu={handleContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        MenuListProps={{
          onContextMenu: handleContextMenu,
        }}
        PaperProps={{
          sx: {
            bgcolor: isDarkTheme ? "#1e293b" : "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 2,
            minWidth: 200,
            border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
          }
        }}
      >
        <MenuItem 
          onClick={handleUploadNewFile}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              bgcolor: isDarkTheme ? "#334155" : "#f8fafc",
            }
          }}
        >
          <UploadIcon sx={{ mr: 1.5, fontSize: 20, color: "#3b82f6" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Upload New File
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={handleCreateNewFolder}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              bgcolor: isDarkTheme ? "#334155" : "#f8fafc",
            }
          }}
        >
          <CreateNewFolderIcon sx={{ mr: 1.5, fontSize: 20, color: "#10b981" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Create New Folder
          </Typography>
        </MenuItem>
      </Menu>

      {/* Folder Context Menu */}
      <Menu
        anchorEl={folderMenuAnchorEl}
        open={Boolean(folderMenuAnchorEl || folderMenuPosition)}
        onClose={handleFolderMenuClose}
        anchorReference={folderMenuPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={
          folderMenuPosition
            ? { top: folderMenuPosition.mouseY, left: folderMenuPosition.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: isDarkTheme ? "#1e293b" : "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 2,
            minWidth: 200,
            border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
          }
        }}
      >
        <MenuItem onClick={handleFolderDownload}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleFolderRenameOpen}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleFolderRemove} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Remove
        </MenuItem>
      </Menu>

      {/* New Menu Dropdown */}
      <Menu
        anchorEl={newMenuAnchorEl}
        open={Boolean(newMenuAnchorEl)}
        onClose={handleNewMenuClose}
        PaperProps={{
          sx: {
            bgcolor: isDarkTheme ? "#1e293b" : "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 2,
            minWidth: 220,
            border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
            mt: 1,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={handleUploadFromMenu}
          sx={{
            py: 1.5,
            px: 2.5,
            "&:hover": {
              bgcolor: isDarkTheme ? "#334155" : "#f8fafc",
            }
          }}
        >
          <UploadIcon sx={{ mr: 2, fontSize: 22, color: "#3b82f6" }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Upload File
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}>
              Upload images, videos, or documents
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={handleCreateFolderFromMenu}
          sx={{
            py: 1.5,
            px: 2.5,
            "&:hover": {
              bgcolor: isDarkTheme ? "#334155" : "#f8fafc",
            }
          }}
        >
          <CreateNewFolderIcon sx={{ mr: 2, fontSize: 22, color: "#10b981" }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              New Folder
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}>
              Organize your assets
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Rename Folder Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleFolderRenameClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Rename Folder
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleFolderRenameSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleFolderRenameClose}>Cancel</Button>
          <Button variant="contained" onClick={handleFolderRenameSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename File Dialog */}
      <Dialog
        open={renameFileDialogOpen}
        onClose={handleFileRenameClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Rename File
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            label="File Name"
            value={renameFileName}
            onChange={(e) => setRenameFileName(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleFileRenameSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleFileRenameClose}>Cancel</Button>
          <Button variant="contained" onClick={handleFileRenameSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderDialogOpen}
        onClose={handleCloseFolderDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: isDarkTheme ? "#fff" : "#1e293b",
            bgcolor: isDarkTheme ? "#1e293b" : "#fff",
            borderBottom: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
          }}
        >
          Create New Folder
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CreateNewFolderIcon sx={{ fontSize: 48, color: "#10b981", mr: 2 }} />
            <Typography variant="body2" sx={{ color: isDarkTheme ? "#94a3b8" : "#64748b" }}>
              Create a new folder in{" "}
              <strong>
                {filterType === "all" ? "All Assets" : filterType === "image" ? "Images" : filterType === "video" ? "Videos" : "Documents"}
              </strong>
            </Typography>
          </Box>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            placeholder="Enter folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateFolder();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: isDarkTheme ? "#0f172a" : "#f8fafc",
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FolderIcon sx={{ color: isDarkTheme ? "#64748b" : "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 2.5, 
            pt: 1,
            borderTop: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
            gap: 1
          }}
        >
          <Button 
            onClick={handleCloseFolderDialog}
            variant="outlined"
            sx={{ 
              color: isDarkTheme ? "#94a3b8" : "#64748b",
              borderColor: isDarkTheme ? "#334155" : "#e2e8f0",
              "&:hover": {
                borderColor: isDarkTheme ? "#475569" : "#cbd5e1",
                backgroundColor: isDarkTheme ? "#334155" : "#f8fafc"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            startIcon={<CreateNewFolderIcon />}
            disabled={!newFolderName.trim()}
            sx={{
              bgcolor: "#10b981",
              "&:hover": { bgcolor: "#059669" },
              "&:disabled": {
                bgcolor: isDarkTheme ? "#334155" : "#cbd5e1",
                color: isDarkTheme ? "#64748b" : "#94a3b8"
              },
              px: 3,
            }}
          >
            Create Folder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketingAssets;